import { adminService } from "../services/adminService";
import { createOverlay } from "./overlay";
import { createStepController } from "./steps";
import { byId } from "./dom";
import type { AuthUser, UserRole } from "../types";

export interface ManageAdminsController {
  open(): void;
}

const ROLE_LABEL: Record<UserRole, string> = {
  user: "User",
  estate_admin: "Estate Admin",
  super_admin: "Super Admin",
};

export function initManageAdmins(): ManageAdminsController {
  const overlay = createOverlay("sheet-manage-admins");
  const root = byId("manage-admins-flow");
  const steps = createStepController(root);

  const searchInput = byId<HTMLInputElement>("admin-search-email");
  const searchError = byId("admin-search-error");
  const searchBtn = byId<HTMLButtonElement>("admin-search-btn");

  const resultName = byId("admin-result-name");
  const resultEmail = byId("admin-result-email");
  const resultEstate = byId("admin-result-estate");
  const resultCurrentRole = byId("admin-result-current-role");
  const newRoleSelect = byId<HTMLSelectElement>("admin-new-role");
  const resultError = byId("admin-result-error");
  const confirmBtn = byId<HTMLButtonElement>("admin-confirm-btn");
  const backBtn = byId("admin-back-btn");

  const successLine = byId("admin-success-line");
  const doneBtn = byId("admin-done-btn");

  const failureReason = byId("admin-failure-reason");
  const retryBtn = byId("admin-retry-btn");

  let foundUser: AuthUser | null = null;

  function resetSearch(): void {
    searchInput.value = "";
    searchError.hidden = true;
    foundUser = null;
  }

  searchBtn.addEventListener("click", async () => {
    const email = searchInput.value.trim();
    if (!email) {
      searchError.textContent = "Enter an email address.";
      searchError.hidden = false;
      return;
    }
    searchError.hidden = true;
    searchBtn.disabled = true;
    searchBtn.textContent = "Looking up\u2026";

    try {
      const user = await adminService.lookupUserByEmail(email);
      if (!user) {
        searchError.textContent = "No account found for that email \u2014 they need to sign up first.";
        searchError.hidden = false;
        return;
      }
      foundUser = user;
      resultName.textContent = user.name;
      resultEmail.textContent = user.email;
      resultEstate.textContent = user.estateName ?? "\u2014";
      resultCurrentRole.textContent = ROLE_LABEL[user.role];
      newRoleSelect.value = user.role;
      resultError.hidden = true;
      steps.show("result");
    } catch (err) {
      searchError.textContent = err instanceof Error ? err.message : "Something went wrong. Please try again.";
      searchError.hidden = false;
    } finally {
      searchBtn.disabled = false;
      searchBtn.textContent = "Look Up";
    }
  });

  backBtn.addEventListener("click", () => steps.show("search"));

  confirmBtn.addEventListener("click", async () => {
    if (!foundUser) return;
    const newRole = newRoleSelect.value as UserRole;

    const confirmed = window.confirm(
      `Change ${foundUser.name}'s role from ${ROLE_LABEL[foundUser.role]} to ${ROLE_LABEL[newRole]}?`
    );
    if (!confirmed) return;

    steps.show("pending");
    try {
      const updated = await adminService.promoteUser({ email: foundUser.email, newRole });
      successLine.textContent = `${updated.name} is now ${ROLE_LABEL[updated.role]}.`;
      steps.show("success");
    } catch (err) {
      failureReason.textContent = err instanceof Error ? err.message : "Couldn't update this account.";
      steps.show("failed");
    }
  });

  retryBtn.addEventListener("click", () => steps.show("result"));

  doneBtn.addEventListener("click", () => {
    resetSearch();
    steps.show("search");
    overlay.close();
  });

  function open(): void {
    resetSearch();
    steps.show("search");
    overlay.open();
    searchInput.focus();
  }

  return { open };
}
